import React from "react";
import {Redirect, Route as BaseRoute} from "react-router-dom";
import {parse as parseQueryString} from "qs";
import {appContainer, Subscribe} from "./contexts/app";

import Login from "./pages/Login";
import Signup from "./pages/Signup";
import PasswordForgot from "./pages/PasswordForgot";
import PasswordReset from "./pages/PasswordReset";
import EmailReset from "./pages/EmailReset";
import AcceptInvitation from "./pages/AcceptInvitation";
import MapPage from "./pages/Map";
import Facility from "./pages/Facility";
import MarkerTypes from "./pages/MarkerTypes";
import MarkerCategories from "./pages/MarkerCategories";
import Account from "./pages/Account";

export const Route = props => {
  props.location.query = parseQueryString(props.location.search, {
    ignoreQueryPrefix: true
  });
  return <BaseRoute {...props} />;
};

export class PreppedRoute extends React.Component {
  render() {
    return (
      <Subscribe
        to={[appContainer]}
        render={app => {
          const permissions = app.state.getIn(["currentUser", "permissions", "edit"]);
          const {requiredPermission} = this.props;
          if (!requiredPermission || permissions[requiredPermission]) {
            return <this.props.component {...this.props._props} />;
          } else {
            return <Redirect to="/account" />;
          }
        }}
      />
    );
  }
}

export const PrivateRoute = ({component: Component, ...rest}) => (
  <Route
    {...rest}
    render={props => {
      return <PreppedRoute requiredPermission={rest.requiredPermission} _props={props} component={Component} />;
    }}
  />
);

export function renderPrivateRoutes(useAll) {
  return privateRoutes.map((route, index) => {
    if (!(route.avail_expired || useAll)) {
      return null;
    }
    return (
      <PrivateRoute
        key={route.key || index}
        exact
        path={route.path}
        component={route.component}
        requiredPermission={route.requiredPermission}
      />
    );
  });
}

export function renderPublicRoutes(onSuccess) {
  return [
    ...publicRoutes.map((route, index) => (
      <Route
        key={route.key || index}
        exact
        path={route.path}
        //onSuccess is a hack until I can unwind this mess
        render={routeProps => <route.component {...routeProps} onSuccess={onSuccess} />}
      />
    )),
    <Route key="defaultLogin" render={() => <Redirect to="/login" />} />
  ];
}

export function renderAvailableRoutes(active, targetPath) {
  let defaultRoute = <Route key="account" render={() => <Redirect to="/account" />} />;
  if (active) {
    defaultRoute = <Route key="default" render={() => <Redirect to={targetPath || "/map"} />} />;
  }

  return [...renderPrivateRoutes(active), defaultRoute];
}

export const privateRoutes = [
  // Current user account
  {
    path: "/account",
    component: Account,
    key: "account",
    avail_expired: true
  },

  // Account Settings
  {
    path: "/account/settings",
    component: Account,
    key: "account",
    requiredPermission: "account",
    avail_expired: true
  },

  // Edit other users
  {
    path: "/account/users",
    component: Account,
    key: "account",
    requiredPermission: "users",
    avail_expired: false
  },
  {
    path: "/account/users/:userId",
    component: Account,
    key: "account",
    requiredPermission: "users",
    avail_expired: false
  },

  // Send invitations (basically the same as users)
  {
    path: "/account/invitations",
    component: Account,
    key: "account",
    requiredPermission: "users"
  },
  {
    path: "/account/invitations/:invitationId",
    component: Account,
    key: "account",
    requiredPermission: "users"
  },

  // Send user feedback
  {
    path: "/account/feedback",
    component: Account,
    key: "account",
    requiredPermission: "users"
  },
  {
    path: "/account/groups",
    component: Account,
    key: "account",
    requiredPermission: "users"
  },

  // The main index map view
  {
    path: "/map/:facilityId/buildings/:buildingId/floors/:floorId",
    component: MapPage,
    key: "map"
  },
  {
    path: "/map/:facilityId/buildings/:buildingId",
    component: MapPage,
    key: "map"
  },
  {
    path: "/map/:facilityId",
    component: MapPage,
    key: "map"
  },
  {
    path: "/map",
    component: MapPage,
    key: "map"
  },

  {
    path: "/facilities/:facilityId/",
    component: Facility,
    key: "facility",
    requiredPermission: "resources"
  },
  {
    path: "/facilities/:facilityId/buildings/:buildingId/floors/:floorId/markers/:markerId/infographics",
    component: Facility,
    key: "facility",
    requiredPermission: "resources"
  },
  {
    path: "/facilities/:facilityId/buildings/:buildingId/floors/:floorId/markers/:markerId",
    component: Facility,
    key: "facility",
    requiredPermission: "resources"
  },
  {
    path: "/facilities/:facilityId/buildings/:buildingId/floors/:floorId/markers/",
    component: Facility,
    key: "facility",
    requiredPermission: "resources"
  },
  {
    path: "/facilities/:facilityId/buildings/:buildingId/floors/:floorId",
    component: Facility,
    key: "facility",
    requiredPermission: "resources"
  },
  {
    path: "/facilities/:facilityId/buildings/:buildingId/floors",
    component: Facility,
    key: "facility",
    requiredPermission: "resources"
  },
  {
    path: "/facilities/:facilityId/buildings/:buildingId",
    component: Facility,
    key: "facility",
    requiredPermission: "resources"
  },
  {
    path: "/facilities/:facilityId/buildings/",
    component: Facility,
    key: "facility",
    requiredPermission: "resources"
  },
  // Menu that contains edit marker types/categories
  {
    path: "/marker-settings",
    component: MarkerTypes,
    key: "markerSettings",
    requiredPermission: "resources"
  },

  {
    path: "/marker-types",
    component: MarkerTypes,
    key: "markerTypes",
    requiredPermission: "resources"
  },
  {
    path: "/marker-types/:markerTypeId",
    component: MarkerTypes,
    key: "markerTypes",
    requiredPermission: "resources"
  },

  // Edit marker type categories
  {
    path: "/marker-categories",
    component: MarkerCategories,
    key: "markerCategories",
    requiredPermission: "resources"
  }
];

export const publicRoutes = [
  {
    path: "/login",
    component: Login,
    key: "login"
  },
  {
    path: "/signup",
    component: Signup,
    key: "signup"
  },
  {
    path: "/forgot-password",
    component: PasswordForgot,
    key: "PasswordForgot"
  },
  {
    path: "/reset-password",
    component: PasswordReset,
    key: "PasswordReset"
  },
  {
    path: "/reset-email",
    component: EmailReset,
    key: "EmailReset"
  },
  {
    path: "/accept-invitation/:invitationId",
    component: AcceptInvitation,
    key: "accept-invitation"
  }
];



// WEBPACK FOOTER //
// ./src/routing.js